/**
 * Dettaglio dei numeri del gruppo "Servizi" (categorie, chi vende cosa, ricorrenti e una tantum, servizi piu' venduti):
 * regole PURE (mappatura righe, filtri, somme, colonne, titoli), senza client Supabase, testate in
 * __tests__/servicesDetail.test.ts. Le stesse regole di salesTargetStore (fetchServiceCategoriesAnalysis,
 * fetchUserCategoryPerformance, fetchServiceTypeTrends, fetchTopServicesPerformance):
 * righe di deal_services di offerte accettate con signed_at nel periodo, ricavo = (scontato ?? listino) * (mesi || 1).
 */
import { isRecurringType, monthKeyLocal } from '../serviceTrends';
import { NO_SERVICE_ID, NO_SERVICE_NAME } from '../topServices';
import { inScope } from '../personScope';
import { DETAIL_MAX_ROWS } from './types';
import type { DetailColumn, DetailResult, DetailRow } from './types';

/** Voce delle categorie per i servizi senza categoria (stessa dello store). */
export const UNCATEGORIZED_ID = '__uncategorized__';
export const UNCATEGORIZED_NAME = 'Senza categoria';

/** Una riga servizio di un'offerta accettata, gia' ripulita dal formato del database. */
export interface ServiceLine {
  id: string;
  dealId: string;
  dealTitle: string;
  clientId: string | null;
  clientName: string;
  signedAt: string;
  assignedTo: string | null;
  assignedName: string;
  serviceId: string | null;
  serviceTitle: string;
  customName: string;
  /** Sempre valorizzata: senza categoria = UNCATEGORIZED_ID, come nello store. */
  categoryId: string;
  categoryName: string;
  serviceType: string | null;
  amount: number;
  discountedAmount: number | null;
  monthlyDuration: number | null;
}

/** Ricavo della riga: lo stesso del grafico. */
export const lineRevenue = (l: Pick<ServiceLine, 'amount' | 'discountedAmount' | 'monthlyDuration'>): number =>
  Number(l.discountedAmount ?? l.amount) * (l.monthlyDuration || 1);

/** Sconto unitario (listino - scontato), come "totalDiscount" dei servizi piu' venduti: lo sconto 100% conta. */
export const lineDiscount = (l: Pick<ServiceLine, 'amount' | 'discountedAmount'>): number =>
  l.discountedAmount != null ? Number(l.amount) - Number(l.discountedAmount) : 0;

/** Riga di deal_services con le relazioni lette dalla query (vedi servicesDetail.ts). */
export function toServiceLine(r: any): ServiceLine {
  const cat = r.services?.service_categories || null;
  const profile = r.deals?.user_profiles;
  return {
    id: r.id,
    dealId: r.deals?.id ?? '',
    dealTitle: r.deals?.title || 'Offerta',
    clientId: r.deals?.client_id ?? null,
    clientName: r.deals?.clients?.business_name || '',
    signedAt: r.deals?.signed_at ?? '',
    assignedTo: r.deals?.assigned_to ?? null,
    assignedName: profile?.full_name || profile?.email || '',
    serviceId: r.service_id ?? null,
    serviceTitle: r.services?.title || '',
    customName: (r.custom_service_name || '').trim(),
    categoryId: cat?.id || UNCATEGORIZED_ID,
    categoryName: cat?.name || UNCATEGORIZED_NAME,
    serviceType: r.services?.service_type ?? null,
    amount: Number(r.amount) || 0,
    discountedAmount: r.discounted_amount == null ? null : Number(r.discounted_amount),
    monthlyDuration: r.monthly_duration ?? null,
  };
}

// ── Filtri: uno per widget ───────────────────────────────────────────────────

/** Categorie: una o piu' (la voce "Altro" riunisce quelle oltre il limite). Senza elenco = tutte. */
export const inCategories = (lines: ServiceLine[], categoryIds: readonly string[] | null): ServiceLine[] =>
  !categoryIds ? lines : lines.filter(l => categoryIds.includes(l.categoryId));

/** Chi vende cosa: solo le offerte assegnate (la persona senza assegnato non compare nel grafico). */
export const forPerson = (lines: ServiceLine[], userId: string, categoryIds: readonly string[] | null): ServiceLine[] =>
  inCategories(lines.filter(l => l.assignedTo === userId), categoryIds);

/** Servizi piu' venduti: stessa regola di raggruppamento di aggregateTopServices (servizio, nome libero, "altre righe"). */
export function topGroupId(l: Pick<ServiceLine, 'serviceId' | 'customName'>): string {
  if (l.serviceId) return l.serviceId;
  return l.customName ? `custom:${l.customName.toLowerCase()}` : NO_SERVICE_ID;
}
export const forTopService = (lines: ServiceLine[], groupId: string): ServiceLine[] =>
  lines.filter(l => topGroupId(l) === groupId);

export type TrendKind = 'recurring' | 'one_time' | 'total';

/** Ricorrenti e una tantum: il mese (AAAA-MM sull'ora locale, come il grafico) e la serie. Senza mese = tutta la finestra. */
export function forTrend(lines: ServiceLine[], monthKey: string | null, kind: TrendKind): ServiceLine[] {
  return lines.filter(l => {
    if (monthKey && monthKeyLocal(new Date(l.signedAt)) !== monthKey) return false;
    if (kind === 'total') return true;
    const recurring = isRecurringType(l.serviceType || 'one_time');
    return kind === 'recurring' ? recurring : !recurring;
  });
}

// ── Colonne e risultato ──────────────────────────────────────────────────────

export const SERVICE_TYPE_LABEL: Record<string, string> = { one_time: 'Una tantum', monthly: 'Mensile', annual: 'Annuale' };
export const serviceTypeLabel = (t: string | null | undefined): string => SERVICE_TYPE_LABEL[t || 'one_time'] ?? 'Una tantum';

export const SERVICE_COLUMNS: DetailColumn[] = [
  { key: 'firmata', label: 'Firmata il', kind: 'date' },
  { key: 'offerta', label: 'Offerta' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'servizio', label: 'Servizio' },
  { key: 'categoria', label: 'Categoria' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'persona', label: 'Persona' },
  { key: 'listino', label: 'Prezzo di listino', kind: 'money' },
  { key: 'sconto', label: 'Sconto', kind: 'money' },
  { key: 'mesi', label: 'Mesi', kind: 'number' },
  { key: 'ricavo', label: 'Ricavo', kind: 'money' },
];

/** Nome mostrato per il servizio: titolo, altrimenti nome libero, altrimenti la voce comune. */
export const serviceName = (l: Pick<ServiceLine, 'serviceTitle' | 'customName'>): string =>
  l.serviceTitle || l.customName || NO_SERVICE_NAME;

/** Somme del dettaglio: lo stesso numero mostrato dal widget (ricavo) piu' listino e sconto. */
export function sumLines(lines: ServiceLine[]): { revenue: number; list: number; discount: number; deals: number } {
  return {
    revenue: lines.reduce((s, l) => s + lineRevenue(l), 0),
    list: lines.reduce((s, l) => s + l.amount, 0),
    discount: lines.reduce((s, l) => s + lineDiscount(l), 0),
    deals: new Set(lines.map(l => l.dealId)).size,
  };
}

/** Piu' recenti prima; a parita' di data, per ricavo. */
const byRecent = (a: ServiceLine, b: ServiceLine) => b.signedAt.localeCompare(a.signedAt) || lineRevenue(b) - lineRevenue(a);

export function serviceLinesResult(lines: ServiceLine[], extraSummary?: string): DetailResult {
  const sums = sumLines(lines);
  const rows: DetailRow[] = [...lines].sort(byRecent).slice(0, DETAIL_MAX_ROWS).map(l => ({
    id: l.id,
    dealId: l.dealId || null,
    cells: {
      firmata: l.signedAt || null,
      offerta: l.dealTitle,
      cliente: l.clientName,
      servizio: serviceName(l),
      categoria: l.categoryName,
      tipo: serviceTypeLabel(l.serviceType),
      persona: l.assignedName,
      listino: l.amount,
      sconto: lineDiscount(l),
      mesi: l.monthlyDuration || 1,
      ricavo: lineRevenue(l),
    },
  }));
  const summary = [lines.length ? `in ${sums.deals} ${sums.deals === 1 ? 'offerta' : 'offerte'}` : '', extraSummary].filter(Boolean).join(', ');
  return {
    rows,
    total: lines.length,
    summary: summary || undefined,
    // Il totale e' su TUTTE le righe (anche oltre quelle mostrate), cosi' coincide sempre col numero del widget
    footer: { firmata: 'Totale', listino: sums.list, sconto: sums.discount, ricavo: sums.revenue },
  };
}

// ── Modalita' "fatturato" (Fatture in Cloud): categorie e chi vende cosa leggono le fatture ─────────

export interface FicLine {
  id: string;
  number: string;
  date: string;
  type: string;
  net: number;
  dealId: string | null;
  dealTitle: string;
  clientId: string | null;
  clientName: string;
  assignedTo: string | null;
  assignedName: string;
  serviceName: string;
  categoryId: string;
  categoryName: string;
}

/** Importo con segno: le note di credito tolgono, come nello store. */
export const ficSigned = (l: Pick<FicLine, 'net' | 'type'>): number => (l.type === 'credit_note' ? -l.net : l.net);

export function toFicLine(r: any): FicLine {
  const cat = r.deal_services?.services?.service_categories || null;
  const profile = r.deals?.user_profiles;
  return {
    id: r.id,
    number: r.number || '',
    date: r.date || '',
    type: r.type,
    net: Number(r.net_amount || 0),
    dealId: r.deal_id ?? null,
    dealTitle: r.deals?.title || '',
    clientId: r.deals?.client_id ?? null,
    clientName: r.deals?.clients?.business_name || '',
    assignedTo: r.deals?.assigned_to ?? null,
    assignedName: profile?.full_name || profile?.email || '',
    serviceName: r.deal_services?.services?.title || r.deal_services?.custom_service_name || '',
    categoryId: cat?.id || UNCATEGORIZED_ID,
    categoryName: cat?.name || UNCATEGORIZED_NAME,
  };
}

/** Filtro persone delle fatture (come filterFicByUsers) e, per chi vende cosa, una persona precisa. */
export function filterFic(lines: FicLine[], userIds: readonly string[], categoryIds: readonly string[] | null, onlyUser?: string): FicLine[] {
  return lines.filter(l => inScope(userIds, l.assignedTo)
    && (!onlyUser || l.assignedTo === onlyUser)
    && (!categoryIds || categoryIds.includes(l.categoryId)));
}

export const FIC_COLUMNS: DetailColumn[] = [
  { key: 'data', label: 'Data fattura', kind: 'date' },
  { key: 'numero', label: 'Numero' },
  { key: 'documento', label: 'Documento' },
  { key: 'offerta', label: 'Offerta' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'servizio', label: 'Servizio' },
  { key: 'categoria', label: 'Categoria' },
  { key: 'persona', label: 'Persona' },
  { key: 'importo', label: 'Importo netto', kind: 'money' },
];

export function ficLinesResult(lines: FicLine[], extraSummary?: string): DetailResult {
  const rows: DetailRow[] = [...lines].sort((a, b) => b.date.localeCompare(a.date)).slice(0, DETAIL_MAX_ROWS).map(l => ({
    id: l.id,
    dealId: l.dealId,
    clientId: l.dealId ? null : l.clientId,
    cells: {
      data: l.date || null,
      numero: l.number,
      documento: l.type === 'credit_note' ? 'Nota di credito' : 'Fattura',
      offerta: l.dealTitle,
      cliente: l.clientName,
      servizio: l.serviceName,
      categoria: l.categoryName,
      persona: l.assignedName,
      importo: ficSigned(l),
    },
  }));
  return {
    rows,
    total: lines.length,
    summary: ['da fatture emesse', extraSummary].filter(Boolean).join(', '),
    footer: { data: 'Totale', importo: lines.reduce((s, l) => s + ficSigned(l), 0) },
  };
}

// ── Titoli e periodo ─────────────────────────────────────────────────────────

const MONTHS_FULL = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const parts = (iso: string) => { const [y, m, d] = iso.slice(0, 10).split('-').map(Number); return { y, m, d }; };
const dmy = (y: number, m: number, d: number) => `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;

/**
 * Etichetta del periodo da due date AAAA-MM-GG con fine ESCLUSA (come le query): un mese intero = "ottobre 2026",
 * altrimenti "01/10/2026 - 15/10/2026". Aritmetica sui numeri, senza fusi.
 */
export function periodLabelIt(startIso: string, endExclusiveIso: string): string {
  const s = parts(startIso);
  const e = parts(endExclusiveIso);
  const lastDay = new Date(Date.UTC(e.y, e.m - 1, e.d - 1));
  const endY = lastDay.getUTCFullYear(), endM = lastDay.getUTCMonth() + 1, endD = lastDay.getUTCDate();
  const wholeMonth = s.d === 1 && endY === s.y && endM === s.m && e.d === 1 && e.y * 12 + e.m === s.y * 12 + s.m + 1;
  if (wholeMonth) return `${MONTHS_FULL[s.m - 1]} ${s.y}`;
  return `${dmy(s.y, s.m, s.d)} - ${dmy(endY, endM, endD)}`;
}

/** Nome file Excel: titolo e periodo. */
export const fileNameOf = (title: string, period: string): string => `${title} ${period}`;

/**
 * Inizio e fine (esclusa) del periodo, identici a quelli di fetchServiceCategoriesAnalysis e simili:
 * intervallo scelto (fine + 1 giorno) oppure il mese 1-12 dell'anno.
 */
export function resolveServicesRange(month: number | undefined, year: number | undefined, dateRange: { from: string; to: string } | null | undefined): { monthStart: string; monthEnd: string } {
  if (dateRange && dateRange.from && dateRange.to) {
    const toDate = new Date(dateRange.to);
    toDate.setDate(toDate.getDate() + 1);
    return { monthStart: dateRange.from, monthEnd: toDate.toISOString().split('T')[0] };
  }
  const m = month && month > 0 && month <= 12 ? month : new Date().getMonth() + 1;
  const y = year || new Date().getFullYear();
  const next = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  return { monthStart: `${y}-${String(m).padStart(2, '0')}-01`, monthEnd: `${nextYear}-${String(next).padStart(2, '0')}-01` };
}
